// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import ISLocale from 'date-fns/locale/is';
import { format } from 'date-fns';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
// Service imports:
import { formatNumber, getAPItoday } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
import { SHORT_MONTHS } from '../../../services/config';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import { IApiHistoryTimeseriesShort, IApiHistoryTimeseriesShortDATA } from '../../../types/MarketTypes';

interface IChartDatum {
    x: number | undefined
    y: number | undefined
};

interface IChartDataInfo {
    name: string,
    data: IChartDatum[]
};

interface IOwnProps {
    title?: string,
    height?: number
};

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const FxMarketChart: React.FC<Props> = ({
    title,
    height = 500,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const today = new Date();
    const from = new Date(today.getFullYear() - 5, today.getMonth() + 1, today.getDate());
    const [EURapiData] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/tradables/EURISK:KELD/history_timeseries_short/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`,
        accessToken,
        refreshRateMs
    );
    const [USDapiData] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/tradables/USDISK:KELD/history_timeseries_short/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`,
        accessToken,
        refreshRateMs
    );
    const [selectedRange, setSelectedRange] = useState(4);

    const handleFxmarketData = (body: IApiHistoryTimeseriesShortDATA) => {
        let dataToReturn: IApiHistoryTimeseriesShort[] = [];
        for(let datum of body.data){
            dataToReturn.push({
                trading_date: datum[0],
                open: datum[1],
                open_yield: datum[2],
                high: datum[3],
                high_yield: datum[4],
                low: datum[5],
                low_yield: datum[6],
                close: datum[7],
                close_yield: datum[8],
                volume: datum[9],
                open_corrected: datum[10],
                high_corrected: datum[11],
                low_corrected: datum[12],
                close_corrected: datum[13],
                volume_corrected: datum[14],
            })
        }
        return dataToReturn;
    }

    const divRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
         //EURISK CLOSING PRICE
         const chartDataInfoEURISK: IChartDataInfo = {
            name: '',
            data: []
        }

        //EURISK VOLUME
        const chartDataInfoVolumeEURISK: IChartDataInfo = {
            name: '',
            data: []
        }

        if (EURapiData.data !== null && !(EURapiData.error instanceof Error)) {
            chartDataInfoEURISK.name = 'EURISK verð';
            chartDataInfoVolumeEURISK.name = 'EURISK magn';

            for (let datum of handleFxmarketData(EURapiData.data)) {
                chartDataInfoEURISK.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.close ?? undefined
                });
                chartDataInfoVolumeEURISK.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.volume ?? undefined
                });
            }
        }

        //USDISK CLOSING PRICE
        const chartDataInfoUSDISK: IChartDataInfo = {
            name: '',
            data: []
        }

        //USDISK VOLUME
        const chartDataInfoVolumeUSDISK: IChartDataInfo = {
            name: '',
            data: []
        }

        if (USDapiData.data !== null && !(USDapiData.error instanceof Error)) {
            chartDataInfoUSDISK.name = 'USDISK verð';
            chartDataInfoVolumeUSDISK.name = 'USDISK magn';
            for (let datum of handleFxmarketData(USDapiData.data)) {
                chartDataInfoUSDISK.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.close ?? undefined
                });
                chartDataInfoVolumeUSDISK.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.volume ?? undefined
                });
            }
        }

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            },
                            y: -6
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000*60*60*24*365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        return `<div class="chart-tooltip">
                            ${this.points?.reduce(function (s, point) {
                                return `${s}<p><span style="color: ${point.color}">${point.series.getName()}</span>: ${formatNumber(point.y)}</p>`;
                            }, `<p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>`)}
                        </div>`;
                        
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000*60*60*24*30,
                    tickWidth: 0
                }],
                yAxis: [{
                    title: {
                        text: 'Verð',
                        offset: 52,
                        x: 5
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    height: '70%',
                    labels: {
                        x: -11,
                        y: 2
                    }
                }, {
                    title: {
                        text: 'Magn',
                        offset: 52,
                        x: 5
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    top: '76%',
                    height: '24%',
                    offset: 0,
                    labels: {
                        x: -11,
                        y: 2
                    }
                }],
                plotOptions: {
                    column: {
                        stacking: 'normal',
                            dataLabels: {
                                enabled: false
                            }
                    },
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 0
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    x: 5,
                    y: 10
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -80
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 26,
                        width: 50
                    },
                    dropdown: 'never',
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    }
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 369
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }, {
                        condition: {
                           callback: function() {
                               return window.matchMedia('(max-width: 767px)').matches;
                           }
                        },
                        chartOptions: {
                            chart: {
                                spacingLeft: 0,
                                spacingRight: 5,
                                marginTop: 40
                            },
                            legend: {
                                x: 10,
                                y: -20,
                                itemStyle: {
                                    fontSize: '12px'
                                }
                            },
                            navigator: {
                                margin: 13
                            },
                            rangeSelector: {
                                buttonPosition: {
                                    y: -40
                                },
                                buttonTheme: {
                                    width: 36,
                                    height: 24
                                }
                            },
                            xAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                }
                            },
                            yAxis: [{
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }, {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }]
                        }
                    }]
                },
                series: [{
                    //EURISK PRICE
                    type: 'line',
                    id: 'EURISK_P',
                    name: chartDataInfoEURISK.name,
                    data: chartDataInfoEURISK.data,
                    yAxis: 0
                }, {
                    //EURISK VOLUME
                    type: 'column',
                    id: 'EURISK_V',
                    name: chartDataInfoVolumeEURISK.name,
                    data: chartDataInfoVolumeEURISK.data,
                    yAxis: 1
                }, {
                    //USDISK PRICE
                    type: 'line',
                    id: 'USDISK_P',
                    name: chartDataInfoUSDISK.name,
                    data: chartDataInfoUSDISK.data,
                    color: '#038600',
                    yAxis: 0
                }, {
                    //USDISK VOLUME
                    type: 'column',
                    id: 'USDISK_V',
                    name: chartDataInfoVolumeUSDISK.name,
                    data: chartDataInfoVolumeUSDISK.data,
                    yAxis: 1,
                    color: '#038600'
                }]
            }, () => {});
        }
    }, [EURapiData, USDapiData, divRef.current]);
    return (
        <DisplayBox 
            className='KCL_chart'
            title={title}
            {...props}
        >
            <div className="chart" ref={divRef}></div>
        </DisplayBox>
    );
}

export default FxMarketChart;